import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    Input,
    OnInit,
    viewChildren,
} from '@angular/core';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
    NumberPipe,
    PaginationComponent,
    DatePipe,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    Sorting,
} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {BehaviorSubject, combineLatest, map, Observable} from 'rxjs';
import {
    ConsumptionAgentResult,
    MeterConsumption,
    PercentageMeterConsumption,
} from '../../consumption-agent-result.model';
import {DateTime} from 'luxon';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {
    TranslatePipe,
    TranslateService,
    keyComparator,
    fromControl,
    composeComparators,
} from '@brunata-angular/util';
import {ColumnHeader, FormGroupFilter, getColumnHeads, sortMeterConsumptions} from './column-heads';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {ConsumptionAgentTranslated} from '../../consumption-agents-overview/consumption-agent.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {uniqBy} from 'lodash-es';
@Component({
    selector: 'app-consumption-agent-result-table',
    templateUrl: './consumption-agent-result-table.component.html',
    styleUrl: './consumption-agent-result-table.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        InputTextGroupComponent,
        InputDateGroupComponent,
        InputDropdownCheckboxesComponent,
        DatePipe,
        TranslatePipe,
        PaginationComponent,
        NumberPipe,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionAgentResultTableComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly translateService = inject(TranslateService);

    private readonly headers = viewChildren(SortableTableHeaderComponent);

    protected readonly resultsSubject$: BehaviorSubject<ConsumptionAgentResult | null> =
        new BehaviorSubject<ConsumptionAgentResult | null>(null);

    @Input({required: true}) set results(value: ConsumptionAgentResult) {
        this.resultsSubject$.next(value);
    }
    @Input() consumptionAgent: ConsumptionAgentTranslated | null = null;
    readonly page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
    readonly pageSize$: BehaviorSubject<number> = new BehaviorSubject<number>(20);
    private sorting$: BehaviorSubject<Sorting> = new BehaviorSubject<Sorting>({
        column: 'defaultSorting',
        direction: 'asc',
    });
    readonly tableSettings$: Observable<{
        sorting: Sorting;
        page: number;
        pageSize: number;
    }> = combineLatest([this.sorting$, this.page$, this.pageSize$]).pipe(
        map(([sorting, page, pageSize]) => ({
            sorting: sorting,
            page: page,
            pageSize: pageSize,
        })),
    );

    protected formGroupFilter: FormGroupFilter = this.formBuilder.group({
        monitoringPeriodStartDate: new FormControl<DateTime | null>(null),
        monitoringPeriodEndDate: new FormControl<DateTime | null>(null),
        comparisonPeriodStartDate: new FormControl<DateTime | null>(null),
        comparisonPeriodEndDate: new FormControl<DateTime | null>(null),
        proBraLoc: new FormControl<string>(''),
        address: new FormControl<string>(''),
        placement: new FormControl<string>(''),
        meterNo: new FormControl<string>(''),
        meterTypeNo: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        alloUnitType: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        unit: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        meterGroupName: new FormControl<SelectableItem[]>([], {nonNullable: true}),
        mountingDate: new FormControl<DateTime | null>(null),
        dismountedDate: new FormControl<DateTime | null>(null),
        replacedBy: new FormControl<string>(''),
        monitoringConsumption: new FormControl<number | null>(null),
        diff: new FormControl<string>(''),
        diffInPercentage: new FormControl<string>(''),
        comparisonConsumption: new FormControl<string>(''),
    });

    private readonly filterControls$: Observable<{
        meterConsumptions: MeterConsumption[] | undefined;
        percentageMeterConsumptions: PercentageMeterConsumption[] | undefined;
        resultSubject: ConsumptionAgentResult | null;
        sorting: Sorting;
        page: number;
        pageSize: number;
    }> = combineLatest([
        this.resultsSubject$,
        this.tableSettings$,
        fromControl(this.formGroupFilter),
    ]).pipe(
        map(([resultsSubject, tableSettings]) => ({
            ...tableSettings,
            resultSubject: resultsSubject,
            meterConsumptions: this.filterMeterConsumptions(
                resultsSubject?.meterConsumptions ?? [],
            ),
            percentageMeterConsumptions: this.filterMeterConsumptions(
                resultsSubject?.percentageMeterConsumptions ?? [],
            ),
        })),
    );

    protected readonly view$: Observable<{
        sortedMeterConsumptions: MeterConsumption[];
        sortedPercentageMeterConsumptions: PercentageMeterConsumption[];
        resultSubject: ConsumptionAgentResult | null;
        sorting: Sorting;
        page: number;
        pageSize: number;
        resultsExists: boolean;
    }> = this.filterControls$.pipe(
        map(filteredConsumptions => ({
            ...filteredConsumptions,
            sortedMeterConsumptions: sortMeterConsumptions(
                filteredConsumptions.meterConsumptions ?? [],
                filteredConsumptions.sorting,
                this.translateService,
            ),
            sortedPercentageMeterConsumptions: sortMeterConsumptions(
                filteredConsumptions.percentageMeterConsumptions ?? [],
                filteredConsumptions.sorting,
                this.translateService,
            ),
            resultsExists: !(
                filteredConsumptions.resultSubject == null ||
                ((filteredConsumptions.resultSubject.meterConsumptions == null ||
                    filteredConsumptions.resultSubject.meterConsumptions.length === 0) &&
                    (filteredConsumptions.resultSubject.percentageMeterConsumptions == null ||
                        filteredConsumptions.resultSubject.percentageMeterConsumptions.length ===
                            0))
            ),
        })),
    );
    protected columnHeads: ColumnHeader[] = getColumnHeads(
        this.formGroupFilter,
        this.translateService,
    );

    ngOnInit(): void {
        this.resultsSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
            let meterConsumptionsLookUp: MeterConsumption[] | PercentageMeterConsumption[] = [];
            if (results?.meterConsumptions != null) {
                meterConsumptionsLookUp = results.meterConsumptions;
            }
            if (results?.percentageMeterConsumptions != null) {
                meterConsumptionsLookUp = results.percentageMeterConsumptions;
            }

            this.formGroupFilter.patchValue({
                meterTypeNo: this.getUniqueValuesForLists(meterConsumptionsLookUp, 'meterTypeNo'),
                unit: this.getUniqueValuesForLists(meterConsumptionsLookUp, 'unit'),
                alloUnitType: this.getUniqueValuesForLists(meterConsumptionsLookUp, 'alloUnitType'),
                meterGroupName: this.getUniqueValuesForLists(
                    meterConsumptionsLookUp,
                    'meterGroupName',
                ),
            });
        });
    }

    private getUniqueValuesForLists(
        consumption: MeterConsumption[] | PercentageMeterConsumption[],
        key: keyof MeterConsumption | PercentageMeterConsumption,
    ): SelectableItem[] {
        const valueList = new Array<SelectableItem>();
        consumption.forEach(agent => {
            if (key === 'meterTypeNo') {
                const translatedValue = this.translateService.translate(
                    'mappers_meterType.' + agent.meterTypeNo,
                    'online',
                    'common',
                );
                valueList.push({name: translatedValue, selected: false});
            }
            if (key === 'unit') {
                valueList.push({
                    name: this.translateService.translate(
                        'mappers_measurementUnit.' + agent.unit.toString(),
                        'online',
                        'common',
                    ),
                    selected: false,
                });
            }

            if (key === 'alloUnitType') {
                const translatedValue =
                    agent.alloUnitType != null
                        ? this.translateService.translate(
                              'mappers_allocationUnitMap.' + agent.alloUnitType,
                              'online',
                              'common',
                          )
                        : '';
                valueList.push({
                    name: translatedValue,
                    selected: false,
                });
            }
            if (key === 'meterGroupName') {
                valueList.push({
                    name: agent.meterGroupName,
                    selected: false,
                });
            }
        });
        return uniqBy(valueList, item => item.name).sort(
            composeComparators([keyComparator(item => item.name)]),
        );
    }

    private filterConsumptionAgentsFromList(
        value: string | number | boolean | null,
        selectableItem: SelectableItem[],
        parameter: string,
    ): boolean {
        const valuesToFilter = selectableItem.filter(it => it.selected).map(it => it.name);
        if (valuesToFilter.length === 0) {
            return true;
        }
        switch (parameter) {
            case 'meterTypeNo':
                return valuesToFilter.includes(
                    this.translateService.translate(
                        'mappers_meterType.' + value,
                        'online',
                        'common',
                    ),
                );
            case 'alloUnitType':
                return valuesToFilter.includes(
                    this.translateService.translate(
                        'mappers_allocationUnitMap.' + value,
                        'online',
                        'common',
                    ),
                );
            case 'unit':
                return valuesToFilter.includes(
                    this.translateService.translate(
                        'mappers_measurementUnit.' + value,
                        'online',
                        'common',
                    ),
                );
            case 'meterGroupName':
                return valuesToFilter.includes(value + '');
            default:
                return true;
        }
    }
    private toLowerFilter(filter: string | number, value: string | number): boolean {
        return value.toString().toLowerCase().includes(filter.toString().toLowerCase());
    }
    private filterMeterConsumptions<T extends MeterConsumption | PercentageMeterConsumption>(
        meterConsumptions: T[],
    ): T[] {
        const formGroupFilter = this.formGroupFilter;
        return meterConsumptions.filter(it => {
            const propertyNo = it.propertyNo?.toString() ?? '';
            const branchNo = it.branchNo?.toString() ?? '';
            const locationNo = it.locationNo?.toString() ?? '';
            const proBraLocValue = `${propertyNo}-${branchNo}-${locationNo}`;
            const address = this.toLowerFilter(
                formGroupFilter.getRawValue().address ?? '',
                it.address ?? '',
            );
            const proBraLoc =
                formGroupFilter.getRawValue().proBraLoc === '' ||
                proBraLocValue.includes(formGroupFilter.getRawValue().proBraLoc ?? '');
            const placement = this.toLowerFilter(
                formGroupFilter.getRawValue().placement ?? '',
                it.placement ?? '',
            );
            const meterNo = this.toLowerFilter(
                formGroupFilter.getRawValue().meterNo ?? '',
                it.meterNo,
            );
            const meterTypeNo = this.filterConsumptionAgentsFromList(
                it.meterTypeNo,
                formGroupFilter.getRawValue().meterTypeNo,
                'meterTypeNo',
            );
            const alloUnitType = this.filterConsumptionAgentsFromList(
                it.alloUnitType,
                formGroupFilter.getRawValue().alloUnitType,
                'alloUnitType',
            );
            const unit = this.filterConsumptionAgentsFromList(
                it.unit,
                formGroupFilter.getRawValue().unit,
                'unit',
            );
            const meterGroupName = this.filterConsumptionAgentsFromList(
                it.meterGroupName,
                formGroupFilter.getRawValue().meterGroupName,
                'meterGroupName',
            );
            const mountingDate =
                formGroupFilter.getRawValue().mountingDate === null ||
                formGroupFilter.getRawValue().mountingDate?.toISODate() ===
                    it.mountingDate?.toISODate();
            const dismountedDate =
                formGroupFilter.getRawValue().dismountedDate === null ||
                formGroupFilter.getRawValue().dismountedDate?.toISODate() ===
                    it.dismountedDate?.toISODate();
            const replacedBy = this.toLowerFilter(
                formGroupFilter.getRawValue().replacedBy ?? '',
                it.replacedBy ?? '',
            );
            const monitoringConsumption = this.toLowerFilter(
                formGroupFilter.getRawValue().monitoringConsumption ?? '',
                it.monitoringConsumption ?? '',
            );
            const diff =
                !('diff' in it) ||
                this.toLowerFilter(formGroupFilter.getRawValue().diff ?? '', it.diff ?? '');

            const diffInPercentage =
                !('diffInPercentage' in it) ||
                this.toLowerFilter(
                    formGroupFilter.getRawValue().diffInPercentage ?? '',
                    it.diffInPercentage ?? '',
                );
            const comparisonConsumption =
                !('comparisonConsumption' in it) ||
                this.toLowerFilter(
                    formGroupFilter.getRawValue().comparisonConsumption ?? '',
                    it.comparisonConsumption ?? '',
                );

            return (
                address &&
                proBraLoc &&
                placement &&
                meterNo &&
                meterTypeNo &&
                alloUnitType &&
                unit &&
                meterGroupName &&
                mountingDate &&
                dismountedDate &&
                replacedBy &&
                monitoringConsumption &&
                diff &&
                diffInPercentage &&
                comparisonConsumption
            );
        });
    }

    protected onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.sorting$.next(sorting);
    }

    protected isPercentageMeterConsumption(
        meterConsumption: MeterConsumption | PercentageMeterConsumption,
    ): meterConsumption is PercentageMeterConsumption {
        return Object.hasOwn(meterConsumption, 'diffInPercentage');
    }
}
